/**
 * Snapchat Ads — Sync Stats HTTP lifecycle (Story 19.8, Task 1).
 *
 * Snap's stats endpoints are synchronous (single GET per report-type),
 * fundamentally different from Microsoft Ads' Submit→Poll→Download lifecycle
 * in `microsoft/reporting.ts` and similar in spirit to Pinterest 19.5's sync
 * `/analytics` flow.
 *
 * The companion module `reports.ts` covers the **async** stats reports flow
 * used when callers need >50 fields per request, level-`creative`/`media`
 * reports, or date ranges that exceed Snap's per-granularity caps (DAY ≤ 31
 * days, HOUR ≤ 7 days). See the sync-vs-async decision tree in
 * `_bmad-output/implementation-artifacts/19-8-snapchat-ads-adapter.md`.
 *
 * Sync stats key facts (verified web research, April 2026):
 *   - Hard cap of 50 fields per request (HTTP 400 when exceeded — message
 *     "Stats endpoint accepts a maximum of 50 fields per request").
 *   - DAY granularity ≤ 31 days; HOUR granularity ≤ 7 days; TOTAL/LIFETIME
 *     unbounded per call.
 *   - Resources are pluralised in URL paths: `adaccounts`, `campaigns`,
 *     `adsquads`, `ads`.
 *   - Single auth header (`Authorization: Bearer ...`) — no Developer Token.
 *
 * @see https://developers.snap.com/api/marketing-api/Ads-API/measurement
 */

import { safeFetch } from "../safe-fetch";
import {
  SNAPCHAT_ADS_API_BASE,
  buildSnapchatHeaders,
} from "./api";
import { SnapchatAdsApiCallError, parseSnapchatAdsApiError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

/** Sync stats requests can take 5-15s server-side; longer than discovery. */
export const SNAPCHAT_SYNC_TIMEOUT_MS = 30_000;

/** Snap's hard cap on fields per stats request (verified web research, April 2026). */
export const SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST = 50 as const;

/** Snap's per-call cap when `granularity=DAY`. Larger ranges fall back to async. */
export const SNAPCHAT_ADS_MAX_DAY_GRANULARITY_DAYS = 31 as const;

/** Snap's per-call cap when `granularity=HOUR`. */
export const SNAPCHAT_ADS_MAX_HOUR_GRANULARITY_DAYS = 7 as const;

/** Cap on `paging.next_link` traversal — defends against agency holdcos. */
export const MAX_CAMPAIGNS_PAGES = 5 as const;

/** Snap's hard upper bound on `?limit=` (200 per docs). */
export const SNAPCHAT_ADS_MAX_PAGE_SIZE = 200 as const;

/**
 * Dimensions that return delivery-only metrics (conversions are always
 * `null`). Single source of truth — `report-builders.ts` reads this list
 * to strip conversion fields before composing requests; the adapter
 * surfaces a non-fatal warning when one is requested.
 *
 * Per integration-mapping-directive line 184:
 *   "Region/DMA/Make breakdowns return **delivery metrics only** (conversions = null)."
 *
 * `lifestyle_category` exhibits the same constraint per Snap docs but isn't
 * exposed in our 16-dimension catalogue — kept here as documentation only.
 */
export const SNAPCHAT_ADS_DELIVERY_ONLY_DIMENSIONS = [
  "region",
  "dma",
  "make",
] as const;

// ============================================================================
// Types
// ============================================================================

/**
 * The 6 sync report-type discriminators stamped onto every record's
 * `_reportType` field. Order is documentation-only — `Promise.all` dispatches
 * in parallel.
 *
 * - `overview`        — account-level, no breakdown (timeseries DAY rows)
 * - `campaigns`       — account-level with `breakdown=campaign`
 * - `adSquads`        — campaign-level with `breakdown=adsquad` (per-campaign fan-out)
 * - `ads`             — adsquad-level with `breakdown=ad`
 * - `ageGender`       — account-level with `report_dimension=age,gender`
 * - `countryOs`       — account-level with `report_dimension=country,os`
 *
 * `adSquads` preserves Snap's vocabulary (NOT "ad groups" / "ad sets").
 * `ageGender` and `countryOs` are the only two combined-dimension breakdowns
 * Snap supports.
 */
export type SnapchatAdsReportType =
  | "overview"
  | "campaigns"
  | "adSquads"
  | "ads"
  | "ageGender"
  | "countryOs";

/** Snap's documented granularity enum — NO `WEEK` or `MONTH`. */
export type SnapchatAdsGranularity = "TOTAL" | "DAY" | "HOUR" | "LIFETIME";

/** Snap's `breakdown=` query param values for hierarchical fan-out. */
export type SnapchatAdsBreakdown = "campaign" | "adsquad" | "ad";

/**
 * Snap's `report_dimension=` query param. Single value OR one of the two
 * combined pairs (`age,gender`, `country,os`) — the only combinations Snap
 * supports.
 */
export type SnapchatAdsReportDimension =
  | "country"
  | "region"
  | "dma"
  | "gender"
  | "age"
  | "os"
  | "make"
  | "lifestyle_category"
  | "country,os"
  | "age,gender";

/**
 * Per-call attribution / source overrides. Defaults match Snap's UI defaults
 * (28-day post-swipe, 1-day post-view, total cross-platform conversion source,
 * conversion-time reporting).
 */
export interface SnapchatAttributionWindows {
  readonly swipeUpAttributionWindow?: "1_DAY" | "7_DAY" | "28_DAY";
  readonly viewAttributionWindow?:
    | "none"
    | "1_HOUR"
    | "3_HOUR"
    | "6_HOUR"
    | "1_DAY"
    | "7_DAY";
  readonly conversionSourceTypes?:
    | "web"
    | "app"
    | "offline"
    | "total"
    | "total_off_platform"
    | "total_on_platform";
  readonly actionReportTime?: "conversion" | "impression";
}

/**
 * Per-call options passed to every sync stats fetcher. `startTime` /
 * `endTime` are pre-encoded ISO 8601 with timezone offset (use
 * `dateToSnapTimestamp` from `report-builders.ts`).
 */
export interface SnapchatAdsStatsOptions extends SnapchatAttributionWindows {
  readonly startTime: string;
  readonly endTime: string;
  readonly fields: ReadonlyArray<string>;
  readonly granularity?: SnapchatAdsGranularity;
  readonly breakdown?: SnapchatAdsBreakdown;
  readonly reportDimension?: SnapchatAdsReportDimension;
  readonly omitEmpty?: boolean;
}

/** One stats time-bucket row (DAY/HOUR granularity) or the flat TOTAL stats object. */
export type SnapchatAdsStatsRow = Record<string, unknown>;

/** Snap's response envelope for a single entity. Both `timeseries_stats` and
 *  `total_stats` shapes appear depending on granularity and broken-down
 *  entity count. */
export interface SnapchatAdsStatsEnvelope {
  readonly request_status?: string;
  readonly request_id?: string;
  readonly timeseries_stats?: ReadonlyArray<{
    readonly timeseries_stat?: {
      readonly id?: string;
      readonly type?: string;
      readonly granularity?: string;
      readonly start_time?: string;
      readonly end_time?: string;
      readonly finalized_data_end_time?: string;
      readonly conversion_data_processed_end_time?: string;
      readonly timeseries?: ReadonlyArray<SnapchatAdsStatsRow>;
    };
  }>;
  readonly total_stats?: ReadonlyArray<{
    readonly total_stat?: {
      readonly id?: string;
      readonly type?: string;
      readonly granularity?: string;
      readonly start_time?: string;
      readonly end_time?: string;
      readonly finalized_data_end_time?: string;
      readonly conversion_data_processed_end_time?: string;
      readonly stats?: SnapchatAdsStatsRow;
      readonly breakdown_stats?: Record<string, ReadonlyArray<SnapchatAdsStatsRow>>;
    };
  }>;
}

// ============================================================================
// URL helpers
// ============================================================================

const ENTITY_PATH: Record<
  "adaccount" | "campaign" | "adsquad" | "ad" | "creative" | "media",
  string
> = {
  adaccount: "adaccounts",
  campaign: "campaigns",
  adsquad: "adsquads",
  ad: "ads",
  creative: "creatives",
  media: "media",
};

/** Build the per-entity stats URL — encodes the entity ID, never the path. */
export function buildStatsUrl(
  entity: keyof typeof ENTITY_PATH,
  entityId: string
): string {
  if (!entityId) {
    throw new Error("Snapchat Ads stats URL requires an entity id");
  }
  const segment = ENTITY_PATH[entity];
  return `${SNAPCHAT_ADS_API_BASE}/${segment}/${encodeURIComponent(entityId)}/stats`;
}

function appendStatsParams(
  url: URL,
  options: SnapchatAdsStatsOptions
): void {
  url.searchParams.set("start_time", options.startTime);
  url.searchParams.set("end_time", options.endTime);
  url.searchParams.set("fields", options.fields.join(","));
  url.searchParams.set("granularity", options.granularity ?? "DAY");
  url.searchParams.set(
    "swipe_up_attribution_window",
    options.swipeUpAttributionWindow ?? "28_DAY"
  );
  url.searchParams.set(
    "view_attribution_window",
    options.viewAttributionWindow ?? "1_DAY"
  );
  url.searchParams.set(
    "conversion_source_types",
    options.conversionSourceTypes ?? "total"
  );
  url.searchParams.set(
    "action_report_time",
    options.actionReportTime ?? "conversion"
  );
  if (options.omitEmpty !== false) {
    url.searchParams.set("omit_empty", "true");
  }
  if (options.breakdown !== undefined) {
    url.searchParams.set("breakdown", options.breakdown);
  }
  if (options.reportDimension !== undefined) {
    url.searchParams.set("report_dimension", options.reportDimension);
  }
}

function validateStatsOptions(options: SnapchatAdsStatsOptions): void {
  if (!options.startTime) {
    throw new Error("Snapchat Ads stats requires startTime");
  }
  if (!options.endTime) {
    throw new Error("Snapchat Ads stats requires endTime");
  }
  if (options.fields.length === 0) {
    throw new Error("Snapchat Ads stats requires at least one field");
  }
  if (options.fields.length > SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST) {
    throw new Error(
      `Snapchat Ads sync stats is limited to ${SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST} fields per request; got ${options.fields.length} — use chunkFieldsForBatching to split`
    );
  }
}

// ============================================================================
// Sync stats fetchers
// ============================================================================

async function getJson(
  url: URL,
  accessToken: string,
  label: string
): Promise<SnapchatAdsStatsEnvelope> {
  const result = await safeFetch(
    url.toString(),
    { method: "GET", headers: buildSnapchatHeaders(accessToken) },
    SNAPCHAT_SYNC_TIMEOUT_MS
  );
  if (!result.success) {
    throw buildStatsApiError(result.error, label);
  }
  try {
    return (await result.data.json()) as SnapchatAdsStatsEnvelope;
  } catch {
    throw new SnapchatAdsApiCallError(
      {
        type: "unknown",
        message: `Snapchat Ads ${label} response was not valid JSON`,
        status: null,
        retryable: false,
      },
      `Snapchat Ads ${label}`
    );
  }
}

/**
 * Generic stats fetcher — routes by entity. Used by the per-pipeline runner
 * via `report-builders.ts:buildSyncStatsRequest`. Pre-validates the options
 * (≤50 fields, non-empty time range) before issuing the round-trip.
 */
export async function fetchSnapchatAdsSyncStats(
  accessToken: string,
  entity: keyof typeof ENTITY_PATH,
  entityId: string,
  options: SnapchatAdsStatsOptions
): Promise<SnapchatAdsStatsEnvelope> {
  validateStatsOptions(options);
  const url = new URL(buildStatsUrl(entity, entityId));
  appendStatsParams(url, options);
  return getJson(url, accessToken, `${entity} stats`);
}

/** Convenience wrapper for the account-level overview pipeline. */
export async function fetchSnapchatAdsAdAccountStats(
  accessToken: string,
  adAccountId: string,
  options: SnapchatAdsStatsOptions
): Promise<SnapchatAdsStatsEnvelope> {
  return fetchSnapchatAdsSyncStats(
    accessToken,
    "adaccount",
    adAccountId,
    options
  );
}

// ============================================================================
// Campaign / adsquad / ad list discovery
// ============================================================================

export interface SnapchatAdsCampaign {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly objective: string;
}

export interface SnapchatAdsCampaignsListResult {
  readonly campaigns: SnapchatAdsCampaign[];
  readonly partialDiscovery: boolean;
  readonly totalCount: number;
}

interface RawCampaignEnvelope {
  campaign?: {
    id?: string;
    name?: string;
    status?: string;
    objective?: string;
  };
}

interface RawCampaignsResponse {
  request_status?: string;
  campaigns?: RawCampaignEnvelope[];
  paging?: { next_link?: string };
}

/**
 * Lists active campaigns for an ad account. Walks `paging.next_link` up to
 * `MAX_CAMPAIGNS_PAGES` (5 × 200 = 1000) before stopping with
 * `partialDiscovery=true`. Filters out PAUSED/DELETED/ARCHIVED at module
 * level so the entity-scoped pipelines don't waste budget on inactive
 * campaigns.
 *
 * `totalCount` reflects the count BEFORE the active-status filter — so a
 * "X of N" UI hint is honest about the OAuth grant size (mirrors `api.ts`
 * H1 fix from Story 19.7).
 */
export async function fetchSnapchatAdsCampaignsList(
  accessToken: string,
  adAccountId: string
): Promise<SnapchatAdsCampaignsListResult> {
  const headers = buildSnapchatHeaders(accessToken);
  const collected: SnapchatAdsCampaign[] = [];
  let totalCount = 0;
  let nextUrl: string | null = (() => {
    const u = new URL(
      `${SNAPCHAT_ADS_API_BASE}/adaccounts/${encodeURIComponent(adAccountId)}/campaigns`
    );
    u.searchParams.set("limit", String(SNAPCHAT_ADS_MAX_PAGE_SIZE));
    return u.toString();
  })();
  let pages = 0;
  let partialDiscovery = false;

  while (nextUrl && pages < MAX_CAMPAIGNS_PAGES) {
    // SSRF defence: validate the Snap-supplied next_link points to Snap's
    // own ads API host before forwarding the bearer token. A compromised /
    // misconfigured Snap response could otherwise leak the token to an
    // attacker-controlled host via the Authorization header.
    if (!isSnapAdsApiUrl(nextUrl)) {
      throw new SnapchatAdsApiCallError(
        {
          type: "unknown",
          message:
            "Snapchat Ads campaigns list paging.next_link points to a non-Snap host — refusing to forward bearer token",
          status: null,
          retryable: false,
        },
        "Snapchat Ads campaigns list"
      );
    }
    const result = await safeFetch(
      nextUrl,
      { method: "GET", headers },
      SNAPCHAT_SYNC_TIMEOUT_MS
    );
    if (!result.success) {
      throw buildStatsApiError(result.error, "campaigns list");
    }
    let json: RawCampaignsResponse;
    try {
      json = (await result.data.json()) as RawCampaignsResponse;
    } catch {
      throw new SnapchatAdsApiCallError(
        {
          type: "unknown",
          message: "Snapchat Ads campaigns list response was not valid JSON",
          status: null,
          retryable: false,
        },
        "Snapchat Ads campaigns list"
      );
    }
    const rawList = Array.isArray(json.campaigns) ? json.campaigns : [];
    for (const entry of rawList) {
      const c = entry.campaign;
      if (!c || typeof c.id !== "string" || c.id.length === 0) continue;
      totalCount += 1;
      const status = typeof c.status === "string" ? c.status : "";
      if (status !== "ACTIVE") continue;
      collected.push({
        id: c.id,
        name: typeof c.name === "string" ? c.name : "",
        status,
        objective: typeof c.objective === "string" ? c.objective : "",
      });
    }
    pages += 1;
    nextUrl = typeof json.paging?.next_link === "string" ? json.paging.next_link : null;
  }

  if (nextUrl) {
    // We hit the page cap before exhausting the cursor — record partial.
    partialDiscovery = true;
  }

  return {
    campaigns: collected,
    partialDiscovery,
    totalCount,
  };
}

/**
 * Validates a URL points to Snap's documented ads API host
 * (`adsapi.snapchat.com`). Used as a defence against SSRF — Snap-supplied
 * pagination URLs flow into `safeFetch` with the bearer token attached, so
 * a host check is required to ensure the token never leaves the trusted
 * boundary. Exact host match (no subdomain wildcards) — Snap docs do NOT
 * use multiple hosts for the data API, so anything that doesn't match is
 * suspicious by definition.
 */
export function isSnapAdsApiUrl(rawUrl: string): boolean {
  try {
    const u = new URL(rawUrl);
    return u.protocol === "https:" && u.host === "adsapi.snapchat.com";
  } catch {
    return false;
  }
}

// ============================================================================
// Field batching (≤50 fields per request)
// ============================================================================

/**
 * Splits a field list into balanced ≤`maxFields` batches. The orchestrator
 * runs each batch as a parallel sub-request and merges results client-side.
 *
 * Returns `[fields]` (single-element array) when the input is already under
 * the cap. Splits into roughly equal halves when over the cap to keep wire
 * payload sizes balanced.
 *
 * @example
 *   chunkFieldsForBatching(["a", "b", "c"], 50) // → [["a", "b", "c"]]
 *   chunkFieldsForBatching(78-field list, 50)   // → [40 fields, 38 fields]
 */
export function chunkFieldsForBatching(
  fields: ReadonlyArray<string>,
  maxFields: number = SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST
): string[][] {
  if (maxFields <= 0) {
    throw new Error("chunkFieldsForBatching: maxFields must be > 0");
  }
  if (fields.length === 0) return [];
  if (fields.length <= maxFields) return [[...fields]];

  // Split into equal-ish halves so each parallel request is similar size.
  // For 78 fields with cap 50: ceil(78/50)=2 batches → 40 + 38.
  const numBatches = Math.ceil(fields.length / maxFields);
  const batchSize = Math.ceil(fields.length / numBatches);
  const batches: string[][] = [];
  for (let i = 0; i < fields.length; i += batchSize) {
    batches.push(fields.slice(i, i + batchSize));
  }
  return batches;
}

// ============================================================================
// Pure response normaliser
// ============================================================================

interface FlattenedRow extends Record<string, unknown> {
  _finalizedDataEndTime?: string;
  _conversionDataProcessedEndTime?: string;
  _entityId?: string;
  _entityType?: string;
}

/**
 * Flattens Snap's stats envelope into `Array<Record<string, unknown>>`.
 *
 * Snap returns three primary shapes:
 *   - DAY/HOUR granularity (timeseries):
 *       `{ timeseries_stats: [{ timeseries_stat: { ..., timeseries: [{ start_time, ...metrics }, ...] }}] }`
 *   - TOTAL granularity (single entity):
 *       `{ total_stats: [{ total_stat: { ..., stats: { ...metrics } } }] }`
 *   - With `breakdown=campaign` / `report_dimension=age,gender`:
 *       Each entity in the array maps to its own breakdown row(s).
 *
 * The normaliser flattens to raw `Record<string, unknown>` rows preserving
 * the entity scope (`_entityId`, `_entityType`) and the ad-account-level
 * finalisation timestamps (`_finalizedDataEndTime`,
 * `_conversionDataProcessedEndTime`) on every row so downstream transformers
 * can stamp `metadata.dataFinalizedThrough`.
 *
 * Returns `[]` for empty/missing envelopes — non-fatal "no data" responses.
 */
export function parseSnapchatAdsSyncResponse(
  json: SnapchatAdsStatsEnvelope,
  _reportType?: SnapchatAdsReportType
): Array<Record<string, unknown>> {
  const out: FlattenedRow[] = [];

  // Path A: timeseries (DAY/HOUR granularity).
  if (Array.isArray(json.timeseries_stats)) {
    for (const wrap of json.timeseries_stats) {
      const stat = wrap.timeseries_stat;
      if (!stat) continue;
      const meta = pickMeta(stat);
      const buckets = Array.isArray(stat.timeseries) ? stat.timeseries : [];
      for (const b of buckets) {
        if (!b || typeof b !== "object") continue;
        out.push({ ...meta, ...b });
      }
    }
  }

  // Path B: total_stats (TOTAL granularity / breakdown / report_dimension).
  if (Array.isArray(json.total_stats)) {
    for (const wrap of json.total_stats) {
      const stat = wrap.total_stat;
      if (!stat) continue;
      const meta = pickMeta(stat);

      // breakdown_stats: { campaign_id: [{ ... }, ...] } or report-dim splits.
      if (stat.breakdown_stats && typeof stat.breakdown_stats === "object") {
        for (const buckets of Object.values(stat.breakdown_stats)) {
          if (!Array.isArray(buckets)) continue;
          for (const b of buckets) {
            if (!b || typeof b !== "object") continue;
            out.push({ ...meta, ...b });
          }
        }
        continue;
      }

      // Plain stats object — single entity TOTAL.
      if (stat.stats && typeof stat.stats === "object") {
        out.push({ ...meta, ...stat.stats });
      }
    }
  }

  return out;
}

function pickMeta(stat: {
  id?: string;
  type?: string;
  finalized_data_end_time?: string;
  conversion_data_processed_end_time?: string;
}): FlattenedRow {
  const meta: FlattenedRow = {};
  if (typeof stat.id === "string") meta._entityId = stat.id;
  if (typeof stat.type === "string") meta._entityType = stat.type;
  if (typeof stat.finalized_data_end_time === "string") {
    meta._finalizedDataEndTime = stat.finalized_data_end_time;
  }
  if (typeof stat.conversion_data_processed_end_time === "string") {
    meta._conversionDataProcessedEndTime =
      stat.conversion_data_processed_end_time;
  }
  return meta;
}

// ============================================================================
// Internal: typed error builder
// ============================================================================

function buildStatsApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  label: string
): SnapchatAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON — fall back to status-only mapping
    }
  }
  const parsed = parseSnapchatAdsApiError(
    parsedBody,
    safeFetchError.providerStatus
  );
  return new SnapchatAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: safeFetchError.message,
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    `Snapchat Ads ${label}`
  );
}

// ============================================================================
// Re-exports for parity with other adapters
// ============================================================================

export { SNAPCHAT_ADS_API_BASE };
